import { OpKind } from '@taquito/rpc';
import { tzip16 } from '@taquito/tzip16';
import { RpcResponseData, RecordMetadata } from '@tezos-domains/core';
import NodeCache from 'node-cache';
import BigNumber from 'bignumber.js';
import { MichelsonMap } from '@taquito/taquito';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class TaquitoClient {
    constructor(tezos, tracer) {
        this.tezos = tezos;
        this.tracer = tracer;
        this.storageCache = new NodeCache({ stdTTL: 60 * 60, checkperiod: 0, useClones: false });
    }
    storage(contractAddress, fresh = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting storage from '${contractAddress}'.`);
            const storage = yield this.cached(contractAddress, () => __awaiter(this, void 0, void 0, function* () {
                const contract = yield this.tezos.wallet.at(contractAddress);
                return contract.storage();
            }), fresh);
            this.tracer.trace(`<= Received storage of '${contractAddress}'.`, storage);
            return storage;
        });
    }
    getBigMapValue(contractAddress, bigMapSelector, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const encodedKey = key.encode();
            this.tracer.trace(`=> Getting big map value from a big map of '${contractAddress}' selected by '${bigMapSelector.toString()}' with key '${encodedKey}'.`);
            const storage = yield this.storage(contractAddress);
            const bigMap = bigMapSelector(storage);
            if (!bigMap) {
                throw new Error(`Specified big map ${bigMapSelector.toString()} does not exist on contract with address ${contractAddress}.`);
            }
            const value = yield bigMap.get(encodedKey);
            this.tracer.trace(`<= Received big map value.`, value);
            return new RpcResponseData(value);
        });
    }
    call(params) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Calling ${JSON.stringify(params)}.`);
            const operation = yield this.tezos.wallet.transfer(params).send();
            this.tracer.trace('<= Operation sent.', operation.opHash);
            return operation;
        });
    }
    params(contractAddress, method, parameters, operationParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Preparing call for entrypoint '${method}' at '${contractAddress}' with parameters '${JSON.stringify(parameters)}' and params '${JSON.stringify(operationParams)}.'`);
            const contract = yield this.tezos.wallet.at(contractAddress);
            const params = contract.methods[method](...parameters).toTransferParams(Object.assign({ mutez: true }, operationParams));
            this.tracer.trace('<= Prepared params.', params);
            return params;
        });
    }
    batch(transactionParams) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Sending batch with ${transactionParams.length} transactions.`, transactionParams);
            const batch = this.tezos.wallet.batch(transactionParams.map(p => (Object.assign({ kind: OpKind.TRANSACTION }, p))));
            const operation = yield batch.send();
            this.tracer.trace('<= Batch sent.', operation.opHash);
            return operation;
        });
    }
    getPkh() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.tezos.wallet.pkh();
        });
    }
    getConstants() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.cached('constants', () => this.tezos.rpc.getConstants());
        });
    }
    executeView(contractAddress, viewName, parameters) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Executing view '${viewName}' at '${contractAddress}' with parameters '${JSON.stringify(parameters)}'.'`);
            const view = yield this.cached(`${contractAddress}:${viewName}`, () => __awaiter(this, void 0, void 0, function* () {
                const contract = yield this.tezos.wallet.at(contractAddress, tzip16);
                const views = yield contract.tzip16().metadataViews();
                return views[viewName]();
            }));
            const result = yield view.executeView(...parameters);
            this.tracer.trace('<= Received view result', result);
            return new RpcResponseData(result);
        });
    }
    cached(key, valueFactory, fresh = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.storageCache.has(key) || fresh) {
                if (fresh) {
                    this.tracer.trace('Forcing reload of value.');
                }
                else {
                    this.tracer.trace('Object not found in cache. Fetching.');
                }
                const promise = valueFactory();
                promise.catch(() => this.storageCache.del(key));
                this.storageCache.set(key, promise);
            }
            else {
                this.tracer.trace('Object found in cache.');
            }
            return this.storageCache.get(key);
        });
    }
}

class BigNumberEncoder {
    encode(value) {
        if (!value) {
            return null;
        }
        return new BigNumber(value);
    }
    decode(value) {
        if (!value) {
            return null;
        }
        return value.toNumber();
    }
}

class MapEncoder {
    encode(value) {
        if (!value) {
            return null;
        }
        const map = new MichelsonMap({ prim: 'map', args: [{ prim: 'string' }, { prim: 'bytes' }] });
        const rawValues = value.raw();
        Object.keys(rawValues).forEach(k => {
            map.set(k, rawValues[k]);
        });
        return map;
    }
    decode(value) {
        if (!value) {
            return null;
        }
        const record = new RecordMetadata();
        value.forEach((v, k) => {
            record.set(k, v);
        });
        return record;
    }
}

export { BigNumberEncoder, MapEncoder, TaquitoClient };
//# sourceMappingURL=taquito.es2015.js.map
